import { Form } from 'antd';
import { useParams } from 'react-router-dom';
import { useCallback, useState } from 'react';
import { OrderStatusType } from 'models/Order';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useOrderByIdQuery, useOrderStatusChangeMutation } from 'api/orders';

export const useDetails = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const [openDetailsEditModal, setOpenDetailsEditModal] = useState(false);
  const [isRejectionModalVisible, setIsRejectionModalVisible] = useState(false);
  const [assignmentModalState, setAssignmentModalState] = useState<boolean>(false);

  const { handleError, handleSuccess } = useNotificationMessages();

  const { data: orderDetails, isLoading } = useOrderByIdQuery({ id: id! }, { skip: !id });
  const [changeStatus] = useOrderStatusChangeMutation();

  const onAssignmentOpen = useCallback(() => setAssignmentModalState(true), []);
  const onAssignmentCancel = useCallback(() => setAssignmentModalState(false), []);

  const onUpdateDetailsClick = () => {
    setOpenDetailsEditModal(true);
  };

  const onCloseDetailsEditModal = () => {
    setOpenDetailsEditModal(false);
  };

  const handleModalCancel = useCallback(() => {
    setIsRejectionModalVisible(false);
    form.resetFields();
  }, [form]);

  const updateOrderStatus = useCallback(
    async (status: OrderStatusType, additionalData = {}) => {
      try {
        const { data, error }: any = await changeStatus({ id, status, ...additionalData });
        if (data?.success) {
          handleSuccess('The order status was successfully changed.');
        } else {
          handleError(error);
        }
      } catch (err) {
        console.log(err, 'error');
        handleError('Something went wrong while changing the status.');
      }
    },
    [handleSuccess, changeStatus, id, handleError]
  );

  const handleRejectionConfirm = useCallback(async () => {
    try {
      const values = await form.validateFields();

      await updateOrderStatus('REJECTED', {
        rejectionReason: values.rejectionReason,
        comments: values.comments,
      });

      setIsRejectionModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  }, [form, updateOrderStatus]);

  const handleStatusChange = useCallback(
    (status: OrderStatusType) => {
      if (status === 'REJECTED') {
        setIsRejectionModalVisible(true);
        return;
      }
      updateOrderStatus(status);
    },
    [updateOrderStatus]
  );

  const isFinalStatus = orderDetails?.isFinalStatus || false;

  return {
    onUpdateDetailsClick,
    onCloseDetailsEditModal,
    orderDetails: orderDetails || {},
    openDetailsEditModal,
    isLoading,
    isFinalStatus,
    handleStatusChange,
    assignmentModalState,
    onAssignmentOpen,
    onAssignmentCancel,
    handleRejectionConfirm,
    handleModalCancel,
    form,
    isRejectionModalVisible,
  };
};
